import * as React from 'react'
// libs
import { useQuery } from '@apollo/client/react'
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
// context
import { TableManagementContext } from 'containers/Search/layers/BL'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// graphql
import { FILTER_SIDEBAR_QUERY } from 'graphql/filter/queries'
import { PLAN_OBJECTIVE_LIST_QUERY } from 'graphql/planObjectives/queries'
// constants
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
// types
import { TRefetch } from 'types/refetch'
import { ConfigItem } from 'layouts/types'
import { NCreatePlanObjective } from 'types/objectives/helpers'
import { FilterTarget, IField, IPlanObjective } from 'types/graphql'
// lodash
import get from 'lodash/get'
import find from 'lodash/fp/find'
import pipe from 'lodash/fp/pipe'
import getFp from 'lodash/fp/get'
// utils
import { safeGetOr } from 'utils/fp'
import { getFieldsList } from 'utils/fields'
import { getSidebarConfig } from 'utils/layouts'

type Props = React.PropsWithChildren<unknown>

interface IContext {
  data: IPlanObjective[]
  config: ConfigItem[]
  fields: IField[]
  refetch: TRefetch<NCreatePlanObjective.IResponse, NCreatePlanObjective.IVariables>
  isLoading: boolean
  pageFilter: string
  totalCount: number
  isRefetching: boolean
}

export const ObjectivesPageContext = React.createContext<IContext>({} as any)

const ObjectivesPageAPI: React.FC<Props> = ({ children }) => {
  const router = useRouter()

  const querySlug = router.query.slug?.[0]

  const { search, filter, page, perPage } = React.useContext(TableManagementContext)

  const { data: filtersQuery, loading } = useQuery(FILTER_SIDEBAR_QUERY, {
    variables: {
      filter: {
        target: FilterTarget.Objective
      }
    }
  })

  const objectEntities = useSelector(objectEntitiesSelector)

  const { data, networkStatus, refetch } = useQuery(PLAN_OBJECTIVE_LIST_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        ...filter,
        description: search,
        filterSlug: querySlug
      },
      pagination: {
        pageSkip: page * perPage,
        pageTake: perPage
      }
    }
  })

  const fields = getFieldsList(objectEntities, OBJECT_ENTITY_NAMES.OBJECTIVE)
  const filters = safeGetOr<any, any, any[]>([], 'filters.data')(filtersQuery)
  const config = getSidebarConfig(filters, 'objectives')

  const pageFilter = React.useMemo(() => {
    if (querySlug) {
      return pipe(
        find(({ slug }) => querySlug === slug),
        getFp('name')
      )(filters)
    }
  }, [filters, querySlug])

  const objectives: IPlanObjective[] = get(data, 'planObjectives.data', [])
  const totalCount: number = get(data, 'planObjectives.pagination.totalCount', 0)

  React.useEffect(() => {
    if (filters.length && !pageFilter) {
      router.push(`/objectives/${filters[0].slug}`)
    }
  }, [filters, pageFilter])

  const value = {
    fields,
    config,
    refetch,
    totalCount,
    pageFilter,
    data: objectives,
    isLoading: loading || networkStatus === 1,
    isRefetching: networkStatus === 2
  } as IContext

  return <ObjectivesPageContext.Provider value={value}>{children}</ObjectivesPageContext.Provider>
}

export default ObjectivesPageAPI
